import { fitPages, formatMoney, pagesWidth, type PageItem } from "@neus/shared";
import { useLayoutEffect, useRef, useState } from "react";
import { HardHatIcon } from "../Icons";
import styles from "./Product.module.css";

export const LOW_STOCK = 5;

/** The safety-yellow "Contractor price" tag. */
export function ContractorTag({ className }: { className?: string }) {
  return (
    <span className={className ? `${styles.contractorTag} ${className}` : styles.contractorTag}>
      <HardHatIcon size={14} /> Contractor price
    </span>
  );
}

/**
 * A price. With `regularPrice` (a contractor price applies), the usual price is struck through and
 * the contractor price is on the line below; otherwise a sale shows its compare-at price.
 */
export function Price({
  price,
  compareAt,
  regularPrice,
}: {
  price: number;
  compareAt?: number;
  regularPrice?: number;
}) {
  if (regularPrice !== undefined) {
    return (
      <span className={styles.contractorPrice}>
        <s className={styles.compareAt} aria-hidden="true">
          {formatMoney(regularPrice)}
        </s>
        <span className={styles.contractorAmount} aria-hidden="true">
          {formatMoney(price)}
        </span>
        <span className="visually-hidden">
          Contractor price {formatMoney(price)}, regularly {formatMoney(regularPrice)}
        </span>
      </span>
    );
  }
  const onSale = compareAt !== undefined && compareAt > price;
  return (
    <span className={styles.price}>
      <span className={onSale ? styles.salePrice : undefined}>{formatMoney(price)}</span>
      {onSale && (
        <>
          <s className={styles.compareAt}>{formatMoney(compareAt)}</s>
          <span className="visually-hidden">Sale price, regularly {formatMoney(compareAt)}</span>
        </>
      )}
    </span>
  );
}

export function StockBadge({ stock }: { stock: number }) {
  if (stock <= 0) return <span className={`${styles.stock} ${styles.stockOut}`}>Out of stock</span>;
  if (stock <= LOW_STOCK) return <span className={`${styles.stock} ${styles.stockLow}`}>Only {stock} left</span>;
  return <span className={`${styles.stock} ${styles.stockIn}`}>In stock</span>;
}

export function QtyStepper({
  value,
  max,
  onChange,
  label = "Quantity",
}: {
  value: number;
  max: number;
  onChange: (qty: number) => void;
  label?: string;
}) {
  const clamp = (n: number) => Math.max(1, Math.min(max, Number.isFinite(n) ? n : 1));
  return (
    <div className={styles.qty} role="group" aria-label={label}>
      <button
        type="button"
        onClick={() => onChange(clamp(value - 1))}
        disabled={value <= 1}
        aria-label="Decrease quantity"
      >
        −
      </button>
      <input
        type="number"
        inputMode="numeric"
        min={1}
        max={max}
        value={value}
        aria-label={label}
        onChange={(e) => onChange(clamp(parseInt(e.target.value, 10)))}
      />
      <button
        type="button"
        onClick={() => onChange(clamp(value + 1))}
        disabled={value >= max}
        aria-label="Increase quantity"
      >
        +
      </button>
    </div>
  );
}

/**
 * Prev, the page numbers and Next, on one line. When the numbers don't all fit, it shows as many of
 * the first and last pages as fit with "…" between them, and the current page between two "…" when
 * it's in the middle (fitPages in shared). When even that needs more room, Prev and Next shrink to
 * their arrows. Widths come from a hidden sample of each kind of button, re-measured whenever the
 * line's width changes.
 */
export function Pagination({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const pages = Math.ceil(total / pageSize);
  const nav = useRef<HTMLElement>(null);
  const sample = useRef<HTMLSpanElement>(null);
  const [fitted, setFitted] = useState<{ items: PageItem[]; arrows: boolean } | null>(null);

  useLayoutEffect(() => {
    const line = nav.current;
    const samples = sample.current;
    if (!line || !samples || pages <= 1) return;
    const fit = () => {
      // Samples: Prev, Next, their arrows, "…", then a page number of each length (1, 10, 100…). Digits
      // are tabular, so every number of a length is as wide as its sample.
      const [prev, next, prevArrow, nextArrow, ellipsis, ...numbers] = [...samples.children].map(
        (el) => el.getBoundingClientRect().width,
      );
      const gap = parseFloat(getComputedStyle(line).columnGap) || 0;
      // Not clientWidth, which rounds: the line would wrap when it rounds up and everything only just fits.
      const lineWidth = line.getBoundingClientRect().width;
      const available = lineWidth - prev! - next! - 2 * gap;
      const width = (item: PageItem) => (item === "gap" ? ellipsis! : numbers[String(item).length - 1]!);
      const items = fitPages(pages, page, width, gap, available);
      if (pagesWidth(items, width, gap) <= available) setFitted({ items, arrows: false });
      else {
        const roomWithArrows = lineWidth - prevArrow! - nextArrow! - 2 * gap;
        setFitted({ items: fitPages(pages, page, width, gap, roomWithArrows), arrows: true });
      }
    };
    fit();
    const resized = new ResizeObserver(fit);
    resized.observe(line);
    return () => resized.disconnect();
  }, [pages, page]);

  if (pages <= 1) return null;
  // Before the first measurement (and without layout, as in tests), every page.
  const shown = fitted?.items ?? Array.from({ length: pages }, (_, i) => i + 1);
  return (
    <nav ref={nav} className={styles.pagination} aria-label="Pagination">
      <button type="button" aria-label="‹ Prev" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        {fitted?.arrows ? "‹" : "‹ Prev"}
      </button>
      {shown.map((p, i) =>
        p === "gap" ? (
          <span key={`gap-${i}`} className={styles.pageGap}>
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            aria-current={p === page ? "page" : undefined}
            className={p === page ? styles.pageActive : undefined}
            onClick={() => onPage(p)}
          >
            {p}
          </button>
        ),
      )}
      <button type="button" aria-label="Next ›" disabled={page >= pages} onClick={() => onPage(page + 1)}>
        {fitted?.arrows ? "›" : "Next ›"}
      </button>
      <span ref={sample} className={styles.pageSamples} aria-hidden="true">
        <button type="button" tabIndex={-1}>
          ‹ Prev
        </button>
        <button type="button" tabIndex={-1}>
          Next ›
        </button>
        <button type="button" tabIndex={-1}>
          ‹
        </button>
        <button type="button" tabIndex={-1}>
          ›
        </button>
        <span className={styles.pageGap}>…</span>
        {Array.from({ length: String(pages).length }, (_, i) => (
          <button key={i} type="button" tabIndex={-1}>
            {"8".repeat(i + 1)}
          </button>
        ))}
      </span>
    </nav>
  );
}
